import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as CompanyIcon,
  Dns as EnvIcon,
  Person as PersonIcon,
  Storage as DbIcon,
  Link as LinkIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { withLocalIngressPort, openTenantServiceUrl } from '../../utils/tenantUrls';

const Section = ({ title, icon, color, bgcolor, border, children }) => (
  <Paper
    elevation={0}
    sx={{
      border: `1px solid ${border}`,
      bgcolor,
      borderRadius: 2,
      overflow: 'hidden',
      height: '100%'
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.75,
        py: 1.1,
        borderBottom: `1px solid ${border}`,
        bgcolor: 'rgba(255,255,255,0.45)'
      }}
    >
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 1.75, py: 1.5 }}>{children}</Box>
  </Paper>
);

const Field = ({ label, value, mono = false }) => (
  <Box sx={{ mb: 1.25 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        wordBreak: 'break-word',
        fontFamily: mono ? 'monospace' : 'inherit'
      }}
    >
      {value != null && value !== '' ? String(value) : '—'}
    </Typography>
  </Box>
);

const statusChip = (status) => {
  const isActive = status === 'active';
  return (
    <Chip
      size="small"
      label={isActive ? 'Active' : String(status || 'inactive').replace(/_/g, ' ')}
      icon={isActive ? <CheckIcon /> : <CancelIcon />}
      sx={{
        fontWeight: 700,
        textTransform: 'capitalize',
        color: '#fff',
        bgcolor: isActive ? '#2e7d32' : '#c62828',
        '& .MuiChip-icon': { color: '#fff' }
      }}
    />
  );
};

/**
 * Blocking modal view for a tenant — no navigation away from the Tenants list.
 * Backdrop / outside clicks do not dismiss; only Close closes it.
 */
const TenantViewDialog = ({ open, tenantId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !tenantId) {
      setTenant(null);
      setError('');
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await api.get(`/tenants/${tenantId}`);
        if (!cancelled) setTenant(response.data?.data || null);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load tenant');
          toast.error('Failed to load tenant details');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, tenantId]);

  const handleDialogClose = (_event, reason) => {
    // Block backdrop click and escape — must use Close button
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    onClose?.();
  };

  const envLabel = tenant?.environment_short_name
    || tenant?.custom_environment_name
    || tenant?.environment
    || '—';

  const contactName = tenant?.contact
    ? `${tenant.contact.first_name || ''} ${tenant.contact.last_name || ''}`.trim() || tenant.contact.email
    : tenant?.contact_email;

  const admins = Array.isArray(tenant?.admin_users) ? tenant.admin_users : [];
  const urls = [
    { label: 'Instance', value: tenant?.instance_url },
    { label: 'Frontend', value: tenant?.frontend_url },
    { label: 'Backend', value: tenant?.backend_url },
    { label: 'API', value: tenant?.api_url }
  ].filter((u) => u.value);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      disableEscapeKeyDown
      aria-labelledby="tenant-view-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          boxShadow: '0 24px 64px rgba(15, 23, 42, 0.35)'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(15, 23, 42, 0.62)',
          backdropFilter: 'blur(2px)'
        }
      }}
    >
      <DialogTitle
        id="tenant-view-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          pr: 1,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #0ea5e9 140%)',
          color: '#fff'
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
            Tenant details
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {tenant?.tenant_name || (loading ? 'Loading…' : 'Tenant')}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', opacity: 0.9, mt: 0.5 }}>
            {tenant?.tenant_id || '—'}
          </Typography>
          {tenant && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
              {statusChip(tenant.status)}
              <Chip size="small" label={`Env: ${envLabel}`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }} />
              <Chip
                size="small"
                label={`Provisioning: ${tenant.provisioning_status || 'not_started'}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }}
              />
            </Box>
          )}
        </Box>
        <IconButton
          aria-label="Close tenant view"
          onClick={onClose}
          sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#f1f5f9', p: 2.5 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && tenant && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Section
                title="Company & identity"
                icon={<CompanyIcon fontSize="small" />}
                color="#1565c0"
                bgcolor="#e3f2fd"
                border="#90caf9"
              >
                <Field label="Company" value={tenant.company_name || tenant.account?.company_name} />
                <Field label="Account" value={tenant.account?.account_name} />
                <Field label="Plan" value={tenant.plan} />
                <Field label="Source" value={tenant.source_type === 'ghe_clone' ? 'GHE Clone' : 'Base Local'} />
              </Section>
            </Grid>

            <Grid item xs={12} md={6}>
              <Section
                title="Environment"
                icon={<EnvIcon fontSize="small" />}
                color="#6a1b9a"
                bgcolor="#f3e5f5"
                border="#ce93d8"
              >
                <Field label="Environment" value={tenant.environment} />
                <Field label="Short name" value={envLabel} mono />
                <Field label="K8s cluster" value={tenant.k8s_cluster_label || tenant.k8s_cluster_id} />
                <Field label="Container prefix" value={tenant.container_prefix} mono />
              </Section>
            </Grid>

            <Grid item xs={12} md={6}>
              <Section
                title="Contact & admins"
                icon={<PersonIcon fontSize="small" />}
                color="#e65100"
                bgcolor="#fff3e0"
                border="#ffcc80"
              >
                <Field label="Primary contact" value={contactName} />
                <Field label="Contact email" value={tenant.contact_email || tenant.contact?.email} />
                <Field label="Phone" value={tenant.contact_phone || tenant.office_direct_number} />
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Admin users
                </Typography>
                {admins.length === 0 && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>—</Typography>
                )}
                {admins.map((a, idx) => (
                  <Typography key={`${a.email || a.name}-${idx}`} variant="body2" sx={{ mt: 0.5 }}>
                    {(a.name || 'Admin') + (a.email ? ` · ${a.email}` : '')}
                  </Typography>
                ))}
              </Section>
            </Grid>

            <Grid item xs={12} md={6}>
              <Section
                title="Database & ports"
                icon={<DbIcon fontSize="small" />}
                color="#00695c"
                bgcolor="#e0f2f1"
                border="#80cbc4"
              >
                <Field label="Database" value={tenant.database_name} mono />
                <Field label="DB / Postgres port" value={tenant.database_port || tenant.postgres_port} />
                <Field label="Frontend port" value={tenant.frontend_port} />
                <Field label="Backend port" value={tenant.backend_port} />
                <Field label="Folder path" value={tenant.folder_path || tenant.target_location} mono />
              </Section>
            </Grid>

            <Grid item xs={12}>
              <Section
                title="Service URLs"
                icon={<LinkIcon fontSize="small" />}
                color="#ad1457"
                bgcolor="#fce4ec"
                border="#f48fb1"
              >
                {urls.length === 0 && <Typography variant="body2">No URLs configured</Typography>}
                {urls.map((u) => {
                  const href = withLocalIngressPort(u.value);
                  return (
                  <Box key={u.label} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {u.label}
                    </Typography>
                    <Typography
                      component="button"
                      type="button"
                      onClick={() => openTenantServiceUrl(u.value)}
                      variant="body2"
                      sx={{
                        display: 'block',
                        wordBreak: 'break-all',
                        color: 'primary.main',
                        background: 'none',
                        border: 0,
                        p: 0,
                        cursor: 'pointer',
                        textAlign: 'left',
                        textDecoration: 'underline',
                        font: 'inherit'
                      }}
                    >
                      {href}
                    </Typography>
                  </Box>
                  );
                })}
              </Section>
            </Grid>

            <Grid item xs={12}>
              <Section
                title="Approval timeline"
                icon={<HistoryIcon fontSize="small" />}
                color="#37474f"
                bgcolor="#eceff1"
                border="#b0bec5"
              >
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: 180 }}>Created</TableCell>
                      <TableCell>
                        {tenant.createdAt ? new Date(tenant.createdAt).toLocaleString() : '—'}
                        {tenant.creator ? ` · ${tenant.creator.first_name} ${tenant.creator.last_name}` : ''}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Client approved</TableCell>
                      <TableCell>
                        {tenant.client_approved_at
                          ? `${new Date(tenant.client_approved_at).toLocaleString()}${tenant.client_approved_by_email ? ` · ${tenant.client_approved_by_email}` : ''}`
                          : '—'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>ERP verified</TableCell>
                      <TableCell>
                        {tenant.erp_verified_at
                          ? `${new Date(tenant.erp_verified_at).toLocaleString()}${tenant.erpVerifier || tenant.verifier ? ` · ${(tenant.erpVerifier || tenant.verifier).first_name} ${(tenant.erpVerifier || tenant.verifier).last_name}` : ''}`
                          : '—'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Final approved</TableCell>
                      <TableCell>
                        {tenant.approved_at
                          ? `${new Date(tenant.approved_at).toLocaleString()}${tenant.approver ? ` · ${tenant.approver.first_name} ${tenant.approver.last_name}` : ''}`
                          : '—'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Section>
            </Grid>

            {tenant.notes && (
              <Grid item xs={12}>
                <Section
                  title="Notes"
                  icon={<SettingsIcon fontSize="small" />}
                  color="#5d4037"
                  bgcolor="#efebe9"
                  border="#bcaaa4"
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{tenant.notes}</Typography>
                </Section>
              </Grid>
            )}

            {(tenant.activityLogs || []).length > 0 && (
              <Grid item xs={12}>
                <Section
                  title="Recent activity"
                  icon={<HistoryIcon fontSize="small" />}
                  color="#283593"
                  bgcolor="#e8eaf6"
                  border="#9fa8da"
                >
                  <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                    {(tenant.activityLogs || []).slice(0, 15).map((log) => (
                      <Box
                        key={log.id}
                        sx={{
                          py: 0.75,
                          borderBottom: '1px dashed rgba(0,0,0,0.08)'
                        }}
                      >
                        <Chip size="small" label={log.action} variant="outlined" sx={{ mr: 1, mb: 0.5 }} />
                        <Typography variant="body2">{log.description}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.createdAt ? new Date(log.createdAt).toLocaleString() : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Section>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#fff' }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          Close this window to return to the tenants list. The page behind stays locked until then.
        </Typography>
        <Button variant="contained" color="primary" onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantViewDialog;
